import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Star, ChevronRight, Pencil } from 'lucide-react'
import { leadsAPI } from './api'
import type { Bookmark } from './api'
import ExportButtons from '../../components/ExportButtons'
import { useKanEksportere } from './eksportTilgang'
import type { ColumnDefinition } from '../../utils/exportToCsv'

const NOTE_MAX = 500

/** Why the org was starred (#627 AC9). Editable on the caller's own bookmark;
 *  a colleague's shared bookmark shows its note read-only. */
function NoteCell({ bookmark, onSave }: { bookmark: Bookmark; onSave: (note: string | null) => Promise<void> }) {
  const { t } = useTranslation('leads')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  if (!bookmark.is_own || !editing) {
    return (
      <div className="flex items-center gap-1 max-w-[260px]">
        <span className="truncate text-sm text-gray-700 dark:text-gray-300" title={bookmark.note ?? ''}>
          {bookmark.note ?? (bookmark.is_own ? '' : '—')}
        </span>
        {bookmark.is_own && (
          <button
            onClick={() => { setDraft(bookmark.note ?? ''); setError(null); setEditing(true) }}
            className="shrink-0 inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
            aria-label={bookmark.note ? t('mineLeads.editNote') : t('mineLeads.addNote')}
          >
            <Pencil className="w-3 h-3" />
            {!bookmark.note && t('mineLeads.add')}
          </button>
        )}
      </div>
    )
  }

  async function save() {
    if (draft.length > NOTE_MAX) { setError(t('mineLeads.maxChars', { n: NOTE_MAX })); return }
    setSaving(true)
    try {
      await onSave(draft.trim() || null)
      setEditing(false)
    } catch {
      setError(t('mineLeads.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-w-[220px]">
      <input
        autoFocus
        value={draft}
        onChange={e => { setDraft(e.target.value); setError(null) }}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        aria-label={t('mineLeads.col.kommentar')}
        className="input-field text-sm w-full"
      />
      <div className="flex items-center gap-2 mt-1 text-xs">
        <button onClick={save} disabled={saving} className="text-primary-600 dark:text-primary-400 hover:underline">{t('mineLeads.save')}</button>
        <button onClick={() => setEditing(false)} className="text-gray-500 hover:underline">{t('mineLeads.cancel')}</button>
        <span className={draft.length > NOTE_MAX ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}>{draft.length}/{NOTE_MAX}</span>
      </div>
      {error && <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
    </div>
  )
}

export default function MineLeadsTab() {
  const { t } = useTranslation('leads')
  const kanEksportere = useKanEksportere()
  const navigate = useNavigate()
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    leadsAPI.getBookmarks()
      .then(res => setBookmarks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  async function handleRemove(org_nr: string) {
    await leadsAPI.removeBookmark(org_nr)
    setBookmarks(prev => prev.filter(b => b.org_nr !== org_nr))
  }

  async function handleToggleShared(org_nr: string, shared: boolean) {
    await leadsAPI.toggleBookmarkShared(org_nr, shared)
    setBookmarks(prev => prev.map(b => b.org_nr === org_nr ? { ...b, shared } : b))
  }

  async function handleSaveNote(org_nr: string, note: string | null) {
    await leadsAPI.setBookmarkNote(org_nr, note)
    setBookmarks(prev => prev.map(b => b.org_nr === org_nr ? { ...b, note } : b))
  }

  /** Mine Leads is where the Biler workflow ends (#625 AC4): search on Biler,
   *  star the organisations worth calling, export them from here. The export
   *  is every bookmark — the tab is not paginated, so that is the whole list. */
  const exportColumns: ColumnDefinition[] = [
    { key: 'navn', label: t('common.bedrift'), format: v => v ?? '' },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'total_score', label: t('common.score'), format: v => (v == null ? '' : String(v)) },
    { key: 'hottest_category', label: t('common.kategori'), format: v => v ?? '' },
    { key: 'follow_up_status', label: t('mineLeads.col.oppfolging'), format: v => v ?? '' },
    { key: 'shared', label: t('mineLeads.col.delt'), format: v => (v ? t('mineLeads.yes') : t('mineLeads.no')) },
    { key: 'note', label: t('mineLeads.col.kommentar'), format: v => v ?? '' },
  ]

  if (loading) return <div className="card p-6 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />

  if (bookmarks.length === 0) {
    return (
      <div className="card p-6 text-center">
        <Star className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-gray-500 dark:text-gray-400">{t('mineLeads.empty')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('mineLeads.emptyHint')}</p>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('mineLeads.count', { n: bookmarks.length })}
        </p>
        {kanEksportere && (
          <ExportButtons
            data={bookmarks}
            columns={exportColumns}
            metadata={{ title: t('common.mineLeads') }}
          />
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="data-table data-table-compact">
          <thead>
            <tr>
              <th className="text-center w-10"></th>
              <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
              <th className="text-left whitespace-nowrap">{t('common.orgNr')}</th>
              <th className="text-right whitespace-nowrap">{t('common.score')}</th>
              <th className="text-left whitespace-nowrap">{t('common.kategori')}</th>
              <th className="text-left whitespace-nowrap">{t('mineLeads.col.oppfolging')}</th>
              <th className="text-center whitespace-nowrap">{t('mineLeads.col.delt')}</th>
              <th className="text-left whitespace-nowrap">{t('mineLeads.col.kommentar')}</th>
              <th className="text-center">{t('common.details')}</th>
            </tr>
          </thead>
          <tbody>
            {bookmarks.map((b, idx) => (
              <tr key={b.org_nr}
                  className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}>
                <td className="text-center">
                  {b.is_own && (
                    <button onClick={() => handleRemove(b.org_nr)} className="text-lg hover:scale-110 transition-transform" title={t('mineLeads.removeTitle')}>
                      &#11088;
                    </button>
                  )}
                </td>
                <td className="font-bold">{b.navn ?? '\u2014'}</td>
                <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{b.org_nr}</td>
                <td className="text-right font-bold">{b.total_score ?? '\u2014'}</td>
                <td>
                  {b.hottest_category && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                      {b.hottest_category}
                    </span>
                  )}
                </td>
                <td className="text-sm text-gray-600 dark:text-gray-400">{b.follow_up_status ?? '\u2014'}</td>
                <td className="text-center">
                  {b.is_own && (
                    <input type="checkbox" checked={b.shared}
                      onChange={e => handleToggleShared(b.org_nr, e.target.checked)}
                      className="rounded-sm" title={t('mineLeads.shareTitle')} />
                  )}
                  {!b.is_own && b.shared && (
                    <span className="text-xs text-gray-400">{t('mineLeads.col.delt')}</span>
                  )}
                </td>
                <td>
                  <NoteCell bookmark={b} onSave={note => handleSaveNote(b.org_nr, note)} />
                </td>
                <td className="text-center">
                  <button onClick={() => navigate(`/leads/bedrift/${b.org_nr}`)}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
                    <span className="text-xs font-medium">{t('common.see')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
